import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Alert, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { adminService, announcementService, timetableService, resultsService, circularsService } from '../services/api';
import config from '../config';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    sidebar_content: '',
    page_number: '',
    status: 'draft'
  });
  const [showModal, setShowModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementFormData, setAnnouncementFormData] = useState({
    title: '',
    announcement_date: '',
    file: null
  });
  const [timetables, setTimetables] = useState([]);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [timetableFormData, setTimetableFormData] = useState({
    academic_year: '',
    semester: '',
    file: null
  });
  const [results, setResults] = useState([]);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsFormData, setResultsFormData] = useState({
    academic_year: '',
    month_semester: '',
    file: null
  });
  const [circulars, setCirculars] = useState([]);
  const [showCircularsModal, setShowCircularsModal] = useState(false);
  const [circularFormData, setCircularFormData] = useState({
    academic_year: '',
    session: '',
    semester: '',
    file: null
  });
  
  // Homepage sections state
  const [homePageSections, setHomePageSections] = useState({
    messageFromPrincipal: { title: '', content: '', image: null },
    briefHistoryExaminationBranch: { title: '', content: '', image: null },
    messageFromControllerExaminations: { title: '', content: '', image: null }
  });
  const [showHomePageModal, setShowHomePageModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchPages();
    fetchAnnouncements();
    fetchTimetables();
    fetchResults();
    fetchCirculars();
    fetchHomePageSections();
  }, [navigate]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPages();
      setPages(response.data.pages);
      const circularsPage = response.data.pages.find(p => p.slug === 'circulars');
      if (circularsPage) {
        setSelectedPageId(circularsPage.id);
        fetchDocuments(circularsPage.id);
      }
    } catch (err) {
      setError('Failed to load pages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (pageId) => {
    try {
      const response = await adminService.getDocuments();
      setDocuments(response.data.documents.filter(doc => doc.page_id === pageId));
    } catch (err) {
      console.error('Failed to load documents', err);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await announcementService.getAllAnnouncements();
      setAnnouncements(response.data.announcements);
    } catch (err) {
      console.error('Failed to load announcements', err);
    }
  };

  const fetchTimetables = async () => {
    try {
      const response = await timetableService.getAllTimetables();
      setTimetables(response.data.timetables);
    } catch (err) {
      console.error('Failed to load timetables', err);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await resultsService.getAllResults();
      setResults(response.data.results);
    } catch (err) {
      console.error('Failed to load results', err);
    }
  };

  const fetchCirculars = async () => {
    try {
      const response = await circularsService.getAllCirculars();
      setCirculars(response.data.circulars);
    } catch (err) {
      console.error('Failed to load circulars', err);
    }
  };

  const fetchHomePageSections = async () => {
    try {
      const slugs = ['messageFromPrincipal', 'briefHistoryExaminationBranch', 'messageFromControllerExaminations'];
      const response = await adminService.getPages();
      const pages = response.data.pages;
      const newSections = { ...homePageSections };

      for (const slug of slugs) {
        const page = pages.find(p => p.slug === slug);
        if (page) {
          newSections[slug] = {
            title: page.title,
            content: page.content,
            image: page.sidebar_content || '',
            id: page.id
          };
        }
      }

      setHomePageSections(newSections);
    } catch (err) {
      console.error('Failed to load homepage sections', err);
    }
  };

  const handleEditHomePageSection = (sectionKey) => {
    setEditingSection(sectionKey);
    setShowHomePageModal(true);
  };

  const handleSaveHomePageSection = async (e) => {
    e.preventDefault();
    if (!editingSection) return;

    try {
      const section = homePageSections[editingSection];
      const dataToSend = {
        title: section.title,
        slug: editingSection,
        content: section.content,
        page_number: 1,
        status: 'published'
      };

      if (section.id) {
        dataToSend.id = section.id;
      }

      const hasNewFile = section.image instanceof File;
      const currentImage = typeof section.image === 'string' ? section.image.trim() : '';
      const isInvalidImageValue = currentImage === '{}' || currentImage === '[object Object]';

      if (hasNewFile) {
        const formDataToSend = new FormData();
        Object.keys(dataToSend).forEach(key => {
          formDataToSend.append(key, dataToSend[key]);
        });
        formDataToSend.append('sidebar_content', section.image);
        await adminService.savePage(formDataToSend);
      } else {
        dataToSend.sidebar_content = isInvalidImageValue ? '' : currentImage;
        await adminService.savePage(dataToSend);
      }

      setShowHomePageModal(false);
      fetchHomePageSections();
      setError(null);
    } catch (err) {
      setError('Failed to save homepage section');
      console.error(err);
    }
  };

  const handleUploadDocument = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    formData.append('page_id', selectedPageId);
    try {
      await adminService.uploadDocument(formData);
      fetchDocuments(selectedPageId);
      event.target.reset();
    } catch (err) {
      console.error('Failed to upload document', err);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await adminService.deleteDocument(id);
        fetchDocuments(selectedPageId);
      } catch (err) {
        console.error('Failed to delete document', err);
      }
    }
  };

  const handleEdit = (page) => {
    setFormData({
      id: page.id,
      title: page.title,
      slug: page.slug,
      content: page.content,
      sidebar_content: page.sidebar_content || '',
      page_number: page.page_number,
      status: page.status
    });
    setEditingId(page.id);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await adminService.savePage(formData);
      setShowModal(false);
      resetForm();
      fetchPages();
      setError(null);
    } catch (err) {
      setError('Failed to save page');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await adminService.deletePage(id);
        fetchPages();
      } catch (err) {
        setError('Failed to delete page');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      sidebar_content: '',
      page_number: '',
      status: 'draft'
    });
    setEditingId(null);
  };

  const handleCreateAnnouncement = () => {
    setAnnouncementFormData({
      title: '',
      announcement_date: new Date().toISOString().split('T')[0],
      file: null
    });
    setShowAnnouncementModal(true);
  };

  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', announcementFormData.title);
      formDataToSend.append('announcement_date', announcementFormData.announcement_date);
      if (announcementFormData.file) {
        formDataToSend.append('file', announcementFormData.file);
      }

      await announcementService.createAnnouncement(formDataToSend);
      setShowAnnouncementModal(false);
      fetchAnnouncements();
      setError(null);
    } catch (err) {
      setError('Failed to save announcement');
      console.error(err);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await announcementService.deleteAnnouncement(id);
        fetchAnnouncements();
      } catch (err) {
        setError('Failed to delete announcement');
        console.error(err);
      }
    }
  };

  const handleCreateTimetable = () => {
    setTimetableFormData({
      academic_year: '',
      semester: '',
      file: null
    });
    setShowTimetableModal(true);
  };

  const handleSaveTimetable = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('academic_year', timetableFormData.academic_year);
      formDataToSend.append('semester', timetableFormData.semester);
      if (timetableFormData.file) {
        formDataToSend.append('file', timetableFormData.file);
      }

      await timetableService.createTimetable(formDataToSend);
      setShowTimetableModal(false);
      fetchTimetables();
      setError(null);
    } catch (err) {
      setError('Failed to save timetable');
      console.error(err);
    }
  };

  const handleDeleteTimetable = async (id) => {
    if (window.confirm('Are you sure you want to delete this timetable?')) {
      try {
        await timetableService.deleteTimetable(id);
        fetchTimetables();
      } catch (err) {
        setError('Failed to delete timetable');
        console.error(err);
      }
    }
  };

  const handleCreateResults = () => {
    setResultsFormData({
      academic_year: '',
      month_semester: '',
      file: null
    });
    setShowResultsModal(true);
  };

  const handleSaveResults = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('academic_year', resultsFormData.academic_year);
      formDataToSend.append('month_semester', resultsFormData.month_semester);
      if (resultsFormData.file) {
        formDataToSend.append('file', resultsFormData.file);
      }

      await resultsService.createResult(formDataToSend);
      setShowResultsModal(false);
      fetchResults();
      setError(null);
    } catch (err) {
      setError('Failed to save result');
      console.error(err);
    }
  };

  const handleDeleteResult = async (id) => {
    if (window.confirm('Are you sure you want to delete this result?')) {
      try {
        await resultsService.deleteResult(id);
        fetchResults();
      } catch (err) {
        setError('Failed to delete result');
        console.error(err);
      }
    }
  };

  const handleCreateCircular = () => {
    setCircularFormData({
      academic_year: '',
      session: '',
      semester: '',
      file: null
    });
    setShowCircularsModal(true);
  };

  const handleSaveCircular = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('academic_year', circularFormData.academic_year);
      formDataToSend.append('session', circularFormData.session);
      formDataToSend.append('semester', circularFormData.semester);
      if (circularFormData.file) {
        formDataToSend.append('file', circularFormData.file);
      }

      await circularsService.createCircular(formDataToSend);
      setShowCircularsModal(false);
      fetchCirculars();
      setError(null);
    } catch (err) {
      setError('Failed to save circular');
      console.error(err);
    }
  };

  const handleDeleteCircular = async (id) => {
    if (window.confirm('Are you sure you want to delete this circular?')) {
      try {
        await circularsService.deleteCircular(id);
        fetchCirculars();
      } catch (err) {
        setError('Failed to delete circular');
        console.error(err);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/admin');
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <Container fluid className="admin-dashboard py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Admin Dashboard</h1>
        <Button variant="danger" onClick={handleLogout}>Logout</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Button
        variant="primary"
        className="mb-3"
        onClick={() => {
          resetForm();
          setShowModal(true);
        }}
      >
        + Add New Page
      </Button>

      <Row>
        <Col>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Page Number</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id}>
                  <td>{page.title}</td>
                  <td>{page.slug}</td>
                  <td>{page.page_number}</td>
                  <td>
                    <span className={`badge bg-${page.status === 'published' ? 'success' : 'warning'}`}>
                      {page.status}
                    </span>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => handleEdit(page)}
                      className="me-2"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(page.id)}
                      className="ms-2"
                    > Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Results Analysis Documents Section */}
      <Row className="mt-5">
        <Col>
          <h2>Results Analysis Documents</h2>
          <Form.Group className="mb-3">
            <Form.Label>Select Page</Form.Label>
            <Form.Select value={selectedPageId || ''} onChange={(e) => {
              const pageId = e.target.value;
              setSelectedPageId(pageId);
              fetchDocuments(pageId);
            }}>
              <option value="">Select a page</option>
              {pages.filter(p => ['circulars', 'about', 'timetables', 'results', 'grievanances'].includes(p.slug)).map(page => (
                <option key={page.id} value={page.id}>{page.title}</option>
              ))}
            </Form.Select>
          </Form.Group>
          {selectedPageId && (
            <>
              <Form onSubmit={handleUploadDocument} className="mb-3">
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Document Title</Form.Label>
                      <Form.Control type="text" name="title" required />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Document File (PDF, DOC, Image)</Form.Label>
                      <Form.Control type="file" name="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" required />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button type="submit" variant="success">Upload Document</Button>
                  </Col>
                </Row>
              </Form>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>File Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.title}</td>
                      <td>{doc.file_type}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => window.open(`${config.UPLOAD_BASE_URL}/${doc.file_path}`, '_blank')}
                          className="me-2"
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
      </Row>

      {/* Home Page Sections */}
      <Row className="mt-5">
        <Col>
          <h2>Home Page Sections</h2>
          <p className="text-muted">Manage the three content sections displayed on the home page</p>
          
          <Row className="mb-4">
            <Col md={4}>
              <div className="card p-3">
                <h5>Message from Principal</h5>
                <p className="text-muted small">
                  <strong>Title:</strong> {homePageSections.messageFromPrincipal.title || 'Not set'}
                </p>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => handleEditHomePageSection('messageFromPrincipal')}
                >
                  Edit
                </Button>
              </div>
            </Col>

            <Col md={4}>
              <div className="card p-3">
                <h5>Brief History</h5>
                <p className="text-muted small">
                  <strong>Title:</strong> {homePageSections.briefHistoryExaminationBranch.title || 'Not set'}
                </p>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => handleEditHomePageSection('briefHistoryExaminationBranch')}
                >
                  Edit
                </Button>
              </div>
            </Col>

            <Col md={4}>
              <div className="card p-3">
                <h5>Message from Controller</h5>
                <p className="text-muted small">
                  <strong>Title:</strong> {homePageSections.messageFromControllerExaminations.title || 'Not set'}
                </p>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => handleEditHomePageSection('messageFromControllerExaminations')}
                >
                  Edit
                </Button>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Announcements Section */}
      <Row className="mt-5">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Controller of Examinations - Announcements</h2>
            <Button variant="success" onClick={handleCreateAnnouncement}>
              <i className="fas fa-plus me-2"></i>Add Announcement
            </Button>
          </div>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((announcement) => (
                <tr key={announcement.id}>
                  <td>{announcement.title}</td>
                  <td>{new Date(announcement.announcement_date).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge bg-${announcement.status === 'published' ? 'success' : 'warning'}`}>
                      {announcement.status}
                    </span>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => window.open(`${config.UPLOAD_BASE_URL}/${announcement.file_path}`, '_blank')}
                      className="me-2"
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Timetables Section */}
      <Row className="mt-5">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Time Tables</h2>
            <Button variant="success" onClick={handleCreateTimetable}>
              <i className="fas fa-plus me-2"></i>Add Timetable
            </Button>
          </div>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Academic Year</th>
                <th>Semester</th>
                <th>Timetable</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {timetables.map((timetable) => (
                <tr key={timetable.id}>
                  <td>{timetable.academic_year}</td>
                  <td>{timetable.semester}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => window.open(`${config.UPLOAD_BASE_URL}/${timetable.file_path}`, '_blank')}
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteTimetable(timetable.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Results Section */}
      <Row className="mt-5">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Results</h2>
            <Button variant="success" onClick={handleCreateResults}>
              <i className="fas fa-plus me-2"></i>Add Result
            </Button>
          </div>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Academic Year</th>
                <th>Month / Semester</th>
                <th>Results</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.id}>
                  <td>{result.academic_year}</td>
                  <td>{result.month_semester}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => window.open(`${config.UPLOAD_BASE_URL}/${result.file_path}`, '_blank')}
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteResult(result.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Circulars Section */}
      <Row className="mt-5">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Circulars</h2>
            <Button variant="success" onClick={handleCreateCircular}>
              <i className="fas fa-plus me-2"></i>Add Circular
            </Button>
          </div>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Academic Year</th>
                <th>Session</th>
                <th>Semester</th>
                <th>Exam Notification</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {circulars.map((circular) => (
                <tr key={circular.id}>
                  <td>{circular.academic_year}</td>
                  <td>{circular.session}</td>
                  <td>{circular.semester}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => window.open(`${config.UPLOAD_BASE_URL}/${circular.file_path}`, '_blank')}
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteCircular(circular.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Page' : 'Add New Page'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Page Number</Form.Label>
              <Form.Control
                type="number"
                value={formData.page_number}
                onChange={(e) => setFormData({ ...formData, page_number: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content (HTML)</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sidebar Content (HTML)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.sidebar_content}
                onChange={(e) => setFormData({ ...formData, sidebar_content: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </Form.Select>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save
              </Button>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Announcement Modal */}
      <Modal show={showAnnouncementModal} onHide={() => setShowAnnouncementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveAnnouncement}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={announcementFormData.title}
                onChange={(e) => setAnnouncementFormData({ ...announcementFormData, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Announcement Date</Form.Label>
              <Form.Control
                type="date"
                value={announcementFormData.announcement_date}
                onChange={(e) => setAnnouncementFormData({ ...announcementFormData, announcement_date: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Attachment (PDF, DOC, DOCX, JPG, PNG)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setAnnouncementFormData({ ...announcementFormData, file: e.target.files[0] })}
                required
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save Announcement
              </Button>
              <Button variant="secondary" onClick={() => setShowAnnouncementModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Timetable Modal */}
      <Modal show={showTimetableModal} onHide={() => setShowTimetableModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Timetable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveTimetable}>
            <Form.Group className="mb-3">
              <Form.Label>Academic Year</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 2023-2024"
                value={timetableFormData.academic_year}
                onChange={(e) => setTimetableFormData({ ...timetableFormData, academic_year: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Semester</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., January 2024"
                value={timetableFormData.semester}
                onChange={(e) => setTimetableFormData({ ...timetableFormData, semester: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Timetable File (PDF, DOC, DOCX, JPG, PNG)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setTimetableFormData({ ...timetableFormData, file: e.target.files[0] })}
                required
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save Timetable
              </Button>
              <Button variant="secondary" onClick={() => setShowTimetableModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Results Modal */}
      <Modal show={showResultsModal} onHide={() => setShowResultsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveResults}>
            <Form.Group className="mb-3">
              <Form.Label>Academic Year</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 2023-2024"
                value={resultsFormData.academic_year}
                onChange={(e) => setResultsFormData({ ...resultsFormData, academic_year: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Month / Semester</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., January 2024"
                value={resultsFormData.month_semester}
                onChange={(e) => setResultsFormData({ ...resultsFormData, month_semester: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Results File (PDF, DOC, DOCX, JPG, PNG)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setResultsFormData({ ...resultsFormData, file: e.target.files[0] })}
                required
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save Result
              </Button>
              <Button variant="secondary" onClick={() => setShowResultsModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Circulars Modal */}
      <Modal show={showCircularsModal} onHide={() => setShowCircularsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Circular</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveCircular}>
            <Form.Group className="mb-3">
              <Form.Label>Academic Year</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 2023-2024"
                value={circularFormData.academic_year}
                onChange={(e) => setCircularFormData({ ...circularFormData, academic_year: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Session</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 2023-2024"
                value={circularFormData.session}
                onChange={(e) => setCircularFormData({ ...circularFormData, session: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Semester</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., January 2024"
                value={circularFormData.semester}
                onChange={(e) => setCircularFormData({ ...circularFormData, semester: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Exam Notification File (PDF, DOC, DOCX, JPG, PNG)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setCircularFormData({ ...circularFormData, file: e.target.files[0] })}
                required
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save Circular
              </Button>
              <Button variant="secondary" onClick={() => setShowCircularsModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Home Page Section Modal */}
      <Modal show={showHomePageModal} onHide={() => setShowHomePageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Home Page Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingSection && (
            <Form onSubmit={handleSaveHomePageSection}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={homePageSections[editingSection].title}
                  onChange={(e) => setHomePageSections({
                    ...homePageSections,
                    [editingSection]: { ...homePageSections[editingSection], title: e.target.value }
                  })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif"
                  onChange={(e) => setHomePageSections({
                    ...homePageSections,
                    [editingSection]: { ...homePageSections[editingSection], image: e.target.files[0] }
                  })}
                />
                {homePageSections[editingSection].image && typeof homePageSections[editingSection].image === 'string' && (
                  <small className="text-muted mt-2 d-block">Current: {homePageSections[editingSection].image}</small>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Content (HTML)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={homePageSections[editingSection].content}
                  onChange={(e) => setHomePageSections({
                    ...homePageSections,
                    [editingSection]: { ...homePageSections[editingSection], content: e.target.value }
                  })}
                  required
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button variant="primary" type="submit">
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setShowHomePageModal(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

    </Container>
  );
};

export default AdminDashboard;
